/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { useStaticQuery, graphql } from 'gatsby';
import { getSrc } from 'gatsby-plugin-image';
import _get from 'lodash/get';
// eslint-disable-next-line import/no-extraneous-dependencies
// eslint-disable-next-line import/no-unresolved
import { useLocation } from '@reach/router';
import { articlesSchema, organizationSchema } from './structuredData';
import initialize from './scroll';
import generalHelper from '../utils/general';

if (!generalHelper.isWindowUndefined()) {
  initialize();
}

const getImageUrl = (image, imageName, allFile) => {
  let imageSrc;
  if (image) {
    imageSrc = getSrc(image);
  }
  else {
    const imageFound = _get(allFile, 'edges', []).find(
      img => _get(img, 'node.relativePath') === imageName
    );

    if (imageFound) {
      imageSrc = getSrc(_get(imageFound, 'node'));
    }
  }

  return imageSrc;
};

let constantData = null;

const SEO = ({
  description, lang, meta, title, type, imageName, image, author, date
}) => {
  // This is to prevent querying all the images and showing only one:
  if (!constantData) {
    constantData = useStaticQuery(
      graphql`
        query {
          site {
            siteMetadata {
              title
              siteUrl
              description
              facebookID
              locale
              keywords
              organization {
                name
                url
                logo
                social
              }
              social {
                twitter
              }
            }
          }
          allFile {
            edges {
              node {
                relativePath
                name
                childImageSharp {
                  gatsbyImageData(quality: 50, layout: CONSTRAINED)
                }
              }
            }
          }
        }
      `
    );
  }
  const { site, allFile } = constantData;
  const imageSrc = getImageUrl(image, imageName, allFile);

  const {
    href: metaUrl,
  } = useLocation();
  const metaDescription = description || _get(site, 'siteMetadata.description');
  const metaKeywords = site.siteMetadata.keywords;
  const {
    facebookID,
    locale,
    social,
    organization,
  } = _get(site, 'siteMetadata');
  const schema = type === 'website'
    ? organizationSchema(organization)
    : articlesSchema(
      { ...image },
      title,
      metaUrl,
      description,
      author,
      organization,
      date
    );


  return (
    <Helmet
      htmlAttributes={{
        lang,
      }}
      title={title}
      meta={[
        {
          name: 'description',
          content: metaDescription,
        },
        {
          name: 'keywords',
          content: metaKeywords.join(','),
        },
        {
          property: 'og:title',
          content: title,
        },
        {
          property: 'og:description',
          content: metaDescription,
        },
        {
          property: 'og:type',
          content: type,
        },
        {
          property: 'og:url',
          content: metaUrl,
        },
        {
          property: 'og:locale',
          content: locale,
        },
        {
          property: 'og:image',
          content: imageSrc,
        },
        {
          property: 'fb:app_id',
          content: facebookID,
        },
        {
          name: 'twitter:card',
          content: 'summary_large_image',
        },
        {
          name: 'twitter:creator',
          content: `@${social.twitter}`,
        },
        {
          name: 'twitter:title',
          content: title,
        },
        {
          name: 'twitter:image',
          content: imageSrc,
        },
        {
          name: 'twitter:description',
          content: metaDescription,
        },
        {
          name: 'viewport',
          content: 'width=device-width, initial-scale=1.0, maximum-scale=1, minimum-scale=1'
        },
      ].concat(meta)}
    >
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
      <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@algolia/autocomplete-theme-classic" />
    </Helmet>
  );
};

SEO.defaultProps = {
  author: '',
  date: {
    published: '',
    modified: '',
  },
  lang: 'en',
  type: 'website',
  meta: [],
  description: '',
  imageName: 'hero_image.png',
  image: null,
};

SEO.propTypes = {
  type: PropTypes.string,
  description: PropTypes.string,
  lang: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  meta: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string.isRequired,
  author: PropTypes.string,
  date: PropTypes.shape({
    published: PropTypes.string,
    modified: PropTypes.string,
  }),
  imageName: PropTypes.string,
  image: PropTypes.shape({
    src: PropTypes.string,
    images: PropTypes.shape({
      fallback: PropTypes.shape({
        src: PropTypes.string,
      })
    })
  }),
};

export default SEO;
